import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, CheckCircle2, Calendar, Clock } from "lucide-react";

const SLOTS = ["09:00", "10:00", "11:00", "14:00", "15:00", "16:00", "17:00"];

export default function ConsultationBook() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    date: "",
    slot: "10:00",
    notes: "",
  });
  const [status, setStatus] = useState("idle");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.date) {
      toast.error("Name, email and date are required");
      return;
    }
    setStatus("loading");
    const payload = { consultant_id: id, ...form };
    try {
      try {
        await fetch("/api/consultation-bookings", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
      } catch { /* offline / mock */ }
      const list = JSON.parse(localStorage.getItem("dyntra_consult_bookings") || "[]");
      list.push({ ...payload, submitted_at: new Date().toISOString() });
      localStorage.setItem("dyntra_consult_bookings", JSON.stringify(list));
      setStatus("success");
      toast.success("Booking confirmed — check your email for details.");
      setTimeout(() => navigate("/consultation"), 1200);
    } catch {
      setStatus("idle");
      toast.error("Could not book — please try again.");
    }
  };

  return (
    <main className="dyntra-container py-10">
      <Link
        to="/consultation"
        data-testid="consult-book-back"
        className="inline-flex items-center gap-2 text-sm font-semibold text-[#4B2AAD] hover:underline"
      >
        <ArrowLeft className="h-4 w-4" /> Back to consultants
      </Link>

      <div className="mt-4 max-w-2xl">
        <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#4B2AAD]">Book Session</p>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-gray-900">Book your consultation</h1>
        <p className="text-sm text-gray-600 mt-2">
          Session confirmations are emailed within an hour. You can reschedule up to 4 hours before the slot.
        </p>

        <form onSubmit={submit} className="mt-6 rounded-2xl border border-gray-100 bg-white shadow-sm p-6 sm:p-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Full name*" testid="book-name" value={form.name} onChange={(v) => set("name", v)} />
            <Field label="Email*" testid="book-email" type="email" value={form.email} onChange={(v) => set("email", v)} />
            <Field label="Phone" testid="book-phone" value={form.phone} onChange={(v) => set("phone", v)} />
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-gray-500 inline-flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5" /> Date*
              </label>
              <input
                data-testid="book-date"
                type="date"
                value={form.date}
                min={new Date().toISOString().slice(0, 10)}
                onChange={(e) => set("date", e.target.value)}
                className="mt-1 w-full rounded-xl border border-gray-200 focus:border-[#4B2AAD] focus:ring-2 focus:ring-[#4B2AAD]/15 px-4 py-2.5 outline-none text-sm"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs font-bold uppercase tracking-wider text-gray-500 inline-flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5" /> Time slot
              </label>
              <div className="mt-1 grid grid-cols-4 sm:grid-cols-7 gap-2">
                {SLOTS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    data-testid={`book-slot-${s}`}
                    onClick={() => set("slot", s)}
                    className={`rounded-lg border py-2 text-sm font-semibold transition-colors ${form.slot === s ? "border-[#4B2AAD] bg-[#F3EFFC] text-[#4B2AAD]" : "border-gray-200 text-gray-700 hover:border-gray-300"}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Notes for the consultant</label>
              <textarea
                data-testid="book-notes"
                rows={4}
                value={form.notes}
                onChange={(e) => set("notes", e.target.value)}
                placeholder="Brief context, chief complaint, or specific questions."
                className="mt-1 w-full rounded-xl border border-gray-200 focus:border-[#4B2AAD] focus:ring-2 focus:ring-[#4B2AAD]/15 px-4 py-2.5 outline-none text-sm"
              />
            </div>
          </div>
          <button
            type="submit"
            data-testid="book-submit"
            disabled={status === "loading"}
            className="dyntra-btn-primary mt-5 w-full sm:w-auto py-3 px-6 text-base"
          >
            {status === "loading" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm booking"}
          </button>
          {status === "success" && (
            <p data-testid="book-success" className="mt-3 inline-flex items-center gap-2 text-sm text-emerald-600 font-semibold">
              <CheckCircle2 className="h-4 w-4" /> Booking confirmed.
            </p>
          )}
        </form>
      </div>
    </main>
  );
}

function Field({ label, testid, value, onChange, type = "text" }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-wider text-gray-500">{label}</label>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-xl border border-gray-200 focus:border-[#4B2AAD] focus:ring-2 focus:ring-[#4B2AAD]/15 px-4 py-2.5 outline-none text-sm"
      />
    </div>
  );
}
